import posthog from 'posthog-js';
import { CONSENT_STORAGE_KEY, getConsent, setConsent } from './consent';

/**
 * Product analytics (PostHog, EU cloud).
 *
 * Entirely no-op unless VITE_POSTHOG_KEY is set, so local dev and tests never
 * send events. Configured conservatively: no autocapture, no session
 * recording, person profiles only for identified (logged-in) users.
 *
 * GDPR / CNIL: PostHog is only initialised once the visitor accepted the cookie
 * banner (see consent.ts); before that no request, cookie or storage is written.
 * Opted out by default as a second safety net, then opted in explicitly.
 *
 * Activation events tracked across the app (the GTM "Aha moment" is derived
 * from these in PostHog: (match_created OR dvw_import_confirmed) AND pdf_exported):
 *  - registration_completed
 *  - match_created
 *  - dvw_import_confirmed
 *  - pdf_exported
 * Tutorials page (VOL-141): tutorial_started / tutorial_completed { tutorial_id, format }.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

let enabled = false;
// Kept even before consent: a user already logged in who accepts later must still be identified.
let currentIdentity: { id: string; props?: Record<string, unknown>; club?: AnalyticsClub | null } | null = null;

export interface AnalyticsClub {
  id: string;
  name: string;
}

// The "club" PostHog group lets feature flags target a club (VOL-132); the backend evaluates
// them with the same group type and key.
function applyIdentity(identity: NonNullable<typeof currentIdentity>): void {
  posthog.identify(identity.id, identity.props);
  if (identity.club) posthog.group('club', identity.club.id, { name: identity.club.name });
}

/** True when the build has a PostHog key, i.e. when the cookie banner has something to ask. */
export function isAnalyticsConfigured(): boolean {
  return Boolean(KEY);
}

/** Starts PostHog at boot only if the visitor already accepted analytics cookies. */
export function initAnalytics(): void {
  if (getConsent() === 'granted') startAnalytics();
}

function startAnalytics(): void {
  if (!KEY) return;
  if (!enabled) {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: false,
      disable_session_recording: true,
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
    });
    enabled = true;
  }
  posthog.opt_in_capturing({ captureEventName: false });
  if (currentIdentity) applyIdentity(currentIdentity);
}

/** Removes PostHog cookies and storage, including those set before consent was required. */
function clearPosthogTraces(): void {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('ph_') || key.startsWith('__ph_'))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing to clear.
  }
  const hostParts = window.location.hostname.split('.');
  // PostHog writes its cookie on the parent domain (e.g. .volleystatspro.com).
  const domains = ['', ...hostParts.slice(0, -1).map((_, i) => `; domain=.${hostParts.slice(i).join('.')}`)];
  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim() ?? '')
    .filter((name) => name.startsWith('ph_'))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
      });
    });
}

// A choice made in another tab applies here too: otherwise automatic pageviews would keep
// flowing (and rewrite PostHog cookies) after a refusal elsewhere.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) return;
    if (getConsent() === 'granted') {
      startAnalytics();
    } else if (enabled) {
      posthog.opt_out_capturing();
      posthog.reset();
      clearPosthogTraces();
    }
  });
}

/** Records the visitor's "accept" and starts analytics. */
export function grantAnalytics(): void {
  setConsent('granted');
  startAnalytics();
}

/** Records the visitor's "refuse"; stops capture and drops PostHog's identifiers if it was running. */
export function denyAnalytics(): void {
  setConsent('denied');
  if (enabled) {
    posthog.opt_out_capturing();
    posthog.reset();
  }
  clearPosthogTraces();
}

/** Associates events with the logged-in user. Call on login/registration. */
export function identifyUser(id: string, props?: Record<string, unknown>, club?: AnalyticsClub | null): void {
  currentIdentity = { id, props, club };
  if (!enabled || getConsent() !== 'granted') return;
  applyIdentity(currentIdentity);
}

/** Clears the identified user. Call on logout. */
export function resetAnalytics(): void {
  currentIdentity = null;
  if (!enabled) return;
  posthog.reset();
}

/** Captures a product event. Silently no-op when analytics is disabled or refused. */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!enabled || getConsent() !== 'granted') return;
  posthog.capture(event, props);
}
